import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import AnnouncementBar from "@/components/layout/AnnouncementBar";
import CartDrawer from "@/components/layout/CartDrawer";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import PartSearch from "@/pages/PartSearch";
import VehicleFinderPage from "@/pages/VehicleFinderPage";
import Cart from "@/pages/Cart";
import PaymentSuccess from "@/pages/PaymentSuccess";
import FitmentAssistance from "@/pages/FitmentAssistance";
import RequestPart from "@/pages/RequestPart";
import Wholesale from "@/pages/Wholesale";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import KnowledgeCenter from "@/pages/KnowledgeCenter";
import Article from "@/pages/Article";
import FAQ from "@/pages/FAQ";
import Policy from "@/pages/Policy";
import NotFound from "@/pages/NotFound";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";

import OwnerLogin from "@/pages/owner/OwnerLogin";
import OwnerLayout from "@/pages/owner/OwnerLayout";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import OwnerProducts from "@/pages/owner/OwnerProducts";
import OwnerProductEdit from "@/pages/owner/OwnerProductEdit";
import OwnerEbay from "@/pages/owner/OwnerEbay";
import OwnerInquiries from "@/pages/owner/OwnerInquiries";
import OwnerOrders from "@/pages/owner/OwnerOrders";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function StoreShell({ children }) {
  return (
    <>
      <AnnouncementBar />
      <Header />
      <CartDrawer />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth session_id in URL fragment during render (before other routes)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Storefront */}
      <Route path="/" element={<StoreShell><Home /></StoreShell>} />
      <Route path="/shop" element={<StoreShell><Shop /></StoreShell>} />
      <Route path="/shop/:category" element={<StoreShell><Shop /></StoreShell>} />
      <Route path="/product/:slug" element={<StoreShell><Product /></StoreShell>} />
      <Route path="/search/part" element={<StoreShell><PartSearch /></StoreShell>} />
      <Route path="/vehicle-finder" element={<StoreShell><VehicleFinderPage /></StoreShell>} />
      <Route path="/cart" element={<StoreShell><Cart /></StoreShell>} />
      <Route path="/payment/success" element={<StoreShell><PaymentSuccess /></StoreShell>} />
      <Route path="/fitment-assistance" element={<StoreShell><FitmentAssistance /></StoreShell>} />
      <Route path="/request-a-part" element={<StoreShell><RequestPart /></StoreShell>} />
      <Route path="/wholesale" element={<StoreShell><Wholesale /></StoreShell>} />
      <Route path="/about" element={<StoreShell><About /></StoreShell>} />
      <Route path="/contact" element={<StoreShell><Contact /></StoreShell>} />
      <Route path="/knowledge" element={<StoreShell><KnowledgeCenter /></StoreShell>} />
      <Route path="/knowledge/:slug" element={<StoreShell><Article /></StoreShell>} />
      <Route path="/faq" element={<StoreShell><FAQ /></StoreShell>} />
      <Route path="/policies/:slug" element={<StoreShell><Policy /></StoreShell>} />
      <Route path="/account/*" element={<StoreShell><Account /></StoreShell>} />
      {/* Owner Cabinet — not in nav */}
      <Route path="/owner-cabinet/login" element={<OwnerLogin />} />
      <Route path="/owner-cabinet" element={<OwnerLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<OwnerDashboard />} />
        <Route path="products" element={<OwnerProducts />} />
        <Route path="products/new" element={<OwnerProductEdit />} />
        <Route path="products/:productId" element={<OwnerProductEdit />} />
        <Route path="ebay" element={<OwnerEbay />} />
        <Route path="inquiries" element={<OwnerInquiries />} />
        <Route path="orders" element={<OwnerOrders />} />
      </Route>
      <Route path="*" element={<StoreShell><NotFound /></StoreShell>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
            <Toaster position="top-right" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
